import React, { useEffect, useMemo, useState } from 'react'
import { CustomTable } from 'components/customTable'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useForm, useWatch } from 'react-hook-form'
import { EyeIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface PersonalizationTabProps {
  data: any
  isLoading: boolean
}

interface WidgetItem {
  title?: string
  thumbnail?: string[]
  short_summary?: string
}

interface WidgetRow {
  id: string
  widgetType: string
  widgetName: string
  widgetIcon: string
  redirectionType: string
  itemNames: string[]
  widgetItems: WidgetItem[]
}

type PaginationState = {
  pageIndex?: number
  pageSize?: number
}

const PersonalizationTab: React.FC<PersonalizationTabProps> = ({ data, isLoading }) => {
  const rowSelectionState = useState<RowSelectionState>({})
  const [selectedWidget, setSelectedWidget] = useState<WidgetRow | null>(null)
  const filterFormHook = useForm<any>({
    defaultValues: {
      pagination: { pageIndex: 0, pageSize: 10 },
      search: '',
      sort: null,
      fields: [],
    },
  })
  const pagination = useWatch({
    control: filterFormHook.control,
    name: 'pagination',
  }) as PaginationState | undefined

  const tableData = useMemo<WidgetRow[]>(() => {
    const widgets = Array.isArray(data?.widgets) ? data.widgets : []
    return widgets.map((widget: any, widgetIndex: number) => {
      const items: WidgetItem[] = Array.isArray(widget?.widget_data?.widget_items)
        ? widget.widget_data.widget_items
        : []

      return {
        id: String(widget?.widget_id ?? widgetIndex),
        widgetTitle: widget?.widget_data?.widget_title ?? '-',
        widgetName: widget?.widget_name ?? '-',
        widgetIcon: widget?.widget_data?.widget_icon ?? '',
        redirectionType: widget?.widget_data?.redirection_to ?? '-',
        itemNames: items.map((item) => item?.title || '-'),
        widgetItems: items,
      }
    })
  }, [data])

  const pageSize = pagination?.pageSize ?? 10
  const pageIndex = pagination?.pageIndex ?? 0

  const pages = useMemo(
    () => Math.max(1, Math.ceil(tableData.length / pageSize)),
    [pageSize, tableData.length]
  )

  const paginatedTableData = useMemo(() => {
    const startIndex = pageIndex * pageSize
    return tableData.slice(startIndex, startIndex + pageSize)
  }, [pageIndex, pageSize, tableData])

  useEffect(() => {
    const maxPageIndex = Math.max(0, pages - 1)
    if (pageIndex > maxPageIndex) {
      filterFormHook.setValue('pagination', {
        pageIndex: maxPageIndex,
        pageSize,
      })
    }
  }, [filterFormHook, pageIndex, pageSize, pages])

  const columns = useMemo<ColumnDef<WidgetRow>[]>(
    () => [
      {
        accessorKey: 'widgetTitle',
        header: 'Widget Title',
        cell: (info) => info.getValue() || '-',
        enableSorting: false,
      },
      // {
      //   accessorKey: 'widgetName',
      //   header: 'Widget Name',
      //   cell: (info) => info.getValue() || '-',
      //   enableSorting: false,
      // },
      // {
      //   accessorKey: 'widgetIcon',
      //   header: 'Widget Icon',
      //   enableSorting: false,
      //   cell: ({ row }) => {
      //     const icon = row.original.widgetIcon
      //     if (!icon) {
      //       return '-'
      //     }
      //     return (
      //       <img
      //         src={icon}
      //         alt={row.original.widgetName}
      //         className="h-9 w-9 rounded-full object-cover border border-gray-200 dark:border-slate-600"
      //       />
      //     )
      //   },
      // },
      // {
      //   accessorKey: 'redirectionType',
      //   header: 'Redirection Type',
      //   cell: (info) => info.getValue() || '-',
      //   enableSorting: false,
      // },
      {
        accessorKey: 'itemNames',
        header: 'Widget Items (Names)',
        enableSorting: false,
        cell: ({ row }) => {
          const names = row.original.itemNames
          if (!names.length) {
            return '-'
          }
          return (
            <div className="flex flex-wrap gap-1.5">
              {names.map((name, idx) => (
                <span
                  key={`${row.original.id}-${idx}`}
                  className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-slate-700 dark:text-blue-200"
                >
                  {name}
                </span>
              ))}
            </div>
          )
        },
      },
      {
        id: 'preview',
        header: 'Preview',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => setSelectedWidget(row.original)}
            className="inline-flex items-center justify-center p-2 rounded-md text-blue-600 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-slate-700"
            title="Preview widget items"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
        ),
      },
    ],
    []
  )

  if (!tableData.length) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No personalization data found.</p>
  }

  return (
    <>
      <CustomTable
        columns={columns}
        data={paginatedTableData}
        pages={pages}
        totalRecords={tableData.length}
        rowSelectionState={rowSelectionState}
        isLoading={isLoading}
        formHook={filterFormHook}
        title="Personalization"
        searchVisible={false}
        useFieldForSelect="id"
      />

      {selectedWidget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl max-h-[85vh] overflow-hidden rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-800 dark:text-white">
                  {selectedWidget.widgetName} - Preview
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Widget Type: {selectedWidget.widgetType}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedWidget(null)}
                className="p-2 rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-slate-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[70vh]">
              {selectedWidget.widgetItems.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedWidget.widgetItems.map((item, index) => {
                    const imageSrc = item?.thumbnail?.[0] || ''
                    return (
                      <div
                        key={`${selectedWidget.id}-item-${index}`}
                        className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900"
                      >
                        <div className="h-40 bg-gray-100 dark:bg-slate-700">
                          {imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={item?.title || 'Widget item'}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                              No thumbnail
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-semibold text-gray-800 dark:text-white">
                            {item?.title || '-'}
                          </p>
                          {item?.short_summary && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-3">
                              {item.short_summary}
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No widget items available for preview.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PersonalizationTab
